/**
 * useMapLayerLongPress Hook - Long-press detection for MapLibre layers
 *
 * Handles long-press gesture on MapLibre GL JS layers (Line, Shape geometries).
 * Uses MapLibre layer events: map.on('touchstart', layerId, ...)
 *
 * NOTE: Movement cancellation is NOT available due to MapLibre limitation
 * (touchmove is not exposed as a layer event). This is a known limitation.
 *
 * Features:
 * - Position capture at touchstart
 * - Timer-based long-press detection
 * - Proper cleanup on touchend/touchcancel
 *
 * @module hooks/gestures/useMapLayerLongPress
 * @example
 * ```typescript
 * useMapLayerLongPress(interactiveLayerId, {
 *   enabled: !isDrawing && isTaskFormOpen,
 *   onLongPress: (position) => showGeometryContextMenu(geometryId, type, position),
 * });
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import { useGeoMarkupStore } from '../../store';
import { selectIsInteractionSuppressed } from '../../store/selectors';
import { INTERACTION_LONG_PRESS_DURATION } from '../../constants';
import type { MapLayerTouchEvent } from 'maplibre-gl';
import type { MapLayerLongPressOptions, MapLayerLongPressReturn } from './types';

/**
 * Hook for detecting long-press gesture on MapLibre layers
 *
 * @param layerId - ID of the MapLibre layer to listen for events
 * @param options - Long-press configuration options
 * @returns Object with clearTimer function
 */
export const useMapLayerLongPress = (
  layerId: string,
  options: MapLayerLongPressOptions
): MapLayerLongPressReturn => {
  const {
    duration = INTERACTION_LONG_PRESS_DURATION,
    enabled = true,
    onLongPress,
  } = options;

  const { current: map } = useMap();

  // Timer ref for cleanup
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Touch start position ref
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  /**
   * Clear long-press timer
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Setup MapLibre layer event listeners
   */
  useEffect(() => {
    if (!map || !enabled) return;

    /**
     * Handle touch start on layer
     */
    const handleTouchStart = (e: MapLayerTouchEvent) => {
      const touch = e.originalEvent.touches[0];
      if (!touch) return;

      // Store touch position
      const position = { x: touch.clientX, y: touch.clientY };
      touchStartPosRef.current = position;

      // Clear any existing timer
      clearTimer();

      // Start long-press timer
      timerRef.current = setTimeout(() => {
        // Skip if interactions are suppressed (panel just closed via click-outside)
        if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
          timerRef.current = null;
          touchStartPosRef.current = null;
          return;
        }

        if (touchStartPosRef.current) {
          onLongPress(touchStartPosRef.current);
        }
      }, duration);
    };

    /**
     * Handle touch end on layer
     */
    const handleTouchEnd = () => {
      clearTimer();
      touchStartPosRef.current = null;
    };

    // Register MapLibre layer events
    // NOTE: touchmove is NOT available as a MapLibre layer event
    map.on('touchstart', layerId, handleTouchStart);
    map.on('touchend', layerId, handleTouchEnd);
    map.on('touchcancel', layerId, handleTouchEnd);

    // Cleanup
    return () => {
      map.off('touchstart', layerId, handleTouchStart);
      map.off('touchend', layerId, handleTouchEnd);
      map.off('touchcancel', layerId, handleTouchEnd);
      clearTimer();
    };
  }, [map, layerId, enabled, duration, onLongPress, clearTimer]);

  return {
    clearTimer,
  };
};
